import { useTranslation } from 'react-i18next'
import { CompetitionCard } from '@/components/competitions/competition-card'
import type { RecentCompetitionViewModel } from '@/features/home/derive-home'
import { formatDateTimeTaipei } from '@/lib/format'
import { competitionRoleLabel } from '@/lib/labels'

/**
 * "最近使用的競賽" — lower visual priority than 我的下一步/我的工作區 (smaller
 * heading, placed after both), but each card still shows *every* role the
 * caller holds on that one competition (`roleBadges`, never collapsed to a
 * single "winning" role) plus its next milestone, reusing the same
 * `CompetitionCard` template every other competition list in the app uses.
 */
export function RecentCompetitionsSection({
  competitions,
}: {
  competitions: RecentCompetitionViewModel[]
}) {
  const { t } = useTranslation(['home', 'admin'])

  return (
    <section className="flex flex-col gap-3" aria-labelledby="recent-competitions-heading">
      <h2 id="recent-competitions-heading" className="text-lg font-semibold tracking-tight">
        {t('recent.title')}
      </h2>
      {competitions.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('recent.empty')}</p>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {competitions.map((competition) => (
            <CompetitionCard
              key={competition.id}
              href={competition.ctaTo}
              status={competition.status}
              name={competition.name}
              summary={competition.shortDescription}
              keyDateLabel={t('recent.nextDeadline')}
              keyDateValue={formatDateTimeTaipei(competition.nextMilestoneAt, {
                withTimezone: true,
              })}
              ctaLabel={t(
                competition.roleBadges.includes('competition_admin')
                  ? 'admin:list.cta.admin'
                  : competition.roleBadges.length > 0
                    ? 'admin:list.cta.participant'
                    : 'admin:list.cta.viewOnly',
              )}
              roleBadges={competition.roleBadges.map((role) => ({
                key: role,
                label: competitionRoleLabel(t, role),
              }))}
              t={t}
            />
          ))}
        </div>
      )}
    </section>
  )
}
